import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { consentService, Consent, api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import type { User } from '@shared/layouts/MainLayout';
import { fetchShared, SHARED_KEYS } from '@shared/services/sharedResource';
import './Consents.css';

const consentLabels: Record<string, string> = {
  TERMS_OF_SERVICE: 'consents.types.termsOfService',
  PRIVACY_POLICY: 'consents.types.privacyPolicy',
  MARKETING: 'consents.types.marketing',
  DATA_PROCESSING: 'consents.types.dataProcessing',
};

function formatIpAddress(value: string | null | undefined): string | undefined {
  if (!value) return undefined;

  const bracketedIpv6 = value.match(/^\[([^\]]+)](?::\d+)?$/);
  if (bracketedIpv6) return bracketedIpv6[1];

  const ipv4WithPort = value.match(/^(\d{1,3}(?:\.\d{1,3}){3}):\d+$/);
  return ipv4WithPort?.[1] ?? value;
}

export default function Consents() {
  const { t, i18n } = useTranslation('compliance');
  const navigate = useNavigate();
  const { showConfirm } = useDialog();
  const [user, setUser] = useState<User | null>(null);
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [listError, setListError] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchUser();
    fetchConsents();
  }, []);

  const fetchUser = async () => {
    setProfileError(false);
    try {
      const data = await fetchShared<{ user: User }>(SHARED_KEYS.profile, async () => {
        const response = await api.get<{ user: User }>('/auth/profile');
        return response.data;
      });
      setUser(data.user);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      console.error('Failed to fetch user:', err);
      setProfileError(true);
    }
  };

  const fetchConsents = async () => {
    try {
      setLoading(true);
      const data = await consentService.getConsents();
      setConsents(data.consents);
      setListError(false);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      console.error('Failed to fetch consents:', err);
      setListError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatConsentType = (type: string): string => {
    const key = consentLabels[type];
    return key
      ? t(key)
      : type
          .split('_')
          .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
          .join(' ');
  };

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));

  const getIpAddressLabel = (consent: Consent) =>
    formatIpAddress(consent.ip_address) || t('consents.table.notAvailable');

  const canRevoke = (type: string): boolean =>
    type !== 'TERMS_OF_SERVICE' && type !== 'PRIVACY_POLICY';
  const isRevoked = (consent: Consent): boolean => !!consent.revoked_at;

  const handleRevokeConsent = async (consentType: string) => {
    if (revoking) return;
    const confirmed = await showConfirm(
      t('consents.revoke.confirmTitle'),
      t('consents.revoke.confirmMessage', { consentType: formatConsentType(consentType) }),
      'warning'
    );
    if (!confirmed) return;

    try {
      setRevoking(consentType);
      setMessage(null);
      await consentService.revokeConsent(consentType);
      setMessage({ type: 'success', text: t('consents.revoke.success') });
      await fetchConsents();
    } catch (err: any) {
      console.error('Failed to revoke consent:', err);
      setMessage({ type: 'error', text: t('consents.errors.revoke') });
    } finally {
      setRevoking(null);
    }
  };

  if (!user) {
    if (profileError)
      return (
        <div
          role="alert"
          className="mx-auto max-w-xl space-y-4 px-4 py-8 text-text-primary-light dark:text-text-primary-dark"
        >
          <p>{t('consents.errors.load')}</p>
          <button
            type="button"
            onClick={fetchUser}
            className="min-h-11 rounded-md px-4 font-semibold text-brand-600"
          >
            {t('common:actions.retry')}
          </button>
        </div>
      );
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 text-center">
        <div role="status" aria-live="polite">
          <span className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          <p className="mt-3 text-sm text-text-muted-light dark:text-text-muted-dark">
            {t('consents.loading')}
          </p>
        </div>
      </div>
    );
  }

  const renderStatus = (consent: Consent) =>
    isRevoked(consent) ? (
      <span className="inline-flex rounded-full bg-state-danger-bg-light px-2.5 py-1 text-xs font-semibold text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark">
        {t('consents.status.revoked', { date: formatDate(consent.revoked_at!) })}
      </span>
    ) : (
      <span className="inline-flex rounded-full bg-state-ok-bg-light px-2.5 py-1 text-xs font-semibold text-state-ok-light dark:bg-state-ok-bg-dark dark:text-state-ok-dark">
        {t('consents.status.active')}
      </span>
    );

  const renderAction = (consent: Consent) =>
    isRevoked(consent) ? (
      <span className="text-sm italic text-text-subtle-light dark:text-text-subtle-dark">
        {t('consents.actions.cannotReinstate')}
      </span>
    ) : canRevoke(consent.type) ? (
      <button
        type="button"
        onClick={() => handleRevokeConsent(consent.type)}
        disabled={revoking !== null}
        className="inline-flex min-h-11 items-center justify-center rounded-md px-3 text-sm font-semibold text-state-danger-light hover:bg-state-danger-bg-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-state-danger-dark dark:hover:bg-state-danger-bg-dark"
      >
        {revoking === consent.type ? t('consents.actions.revoking') : t('consents.actions.revoke')}
      </button>
    ) : (
      <span
        className="text-sm font-medium text-text-subtle-light dark:text-text-subtle-dark"
        title={t('consents.actions.requiredDescription')}
      >
        {t('consents.actions.required')}
      </span>
    );

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
          {t('consents.title')}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-text-muted-light dark:text-text-muted-dark sm:text-base">
          {t('consents.subtitle')}
        </p>
      </header>

      {message && (
        <div
          role={message.type === 'error' ? 'alert' : 'status'}
          className={`mb-6 flex items-start justify-between gap-3 rounded-lg border p-4 ${
            message.type === 'success'
              ? 'border-state-ok-light/20 bg-state-ok-bg-light text-state-ok-light dark:border-state-ok-dark/20 dark:bg-state-ok-bg-dark dark:text-state-ok-dark'
              : 'border-state-danger-light/20 bg-state-danger-bg-light text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark'
          }`}
        >
          <p className="text-sm">{message.text}</p>
          <button
            type="button"
            onClick={() => setMessage(null)}
            className="min-h-11 shrink-0 rounded px-2 text-sm font-medium underline hover:no-underline"
          >
            {t('consents.actions.dismiss')}
          </button>
        </div>
      )}

      {listError && !loading ? (
        <div
          role="alert"
          className="space-y-3 rounded-xl border border-surface-border-light p-4 dark:border-surface-border-dark"
        >
          <p>{t('consents.errors.load')}</p>
          <button
            type="button"
            onClick={fetchConsents}
            className="min-h-11 rounded-md px-3 font-semibold text-brand-600"
          >
            {t('common:actions.retry')}
          </button>
        </div>
      ) : loading ? (
        <div
          className="rounded-xl border border-surface-border-light bg-surface-card-light py-12 text-center dark:border-surface-border-dark dark:bg-surface-card-dark"
          role="status"
          aria-live="polite"
        >
          <span className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          <p className="mt-3 text-sm text-text-muted-light dark:text-text-muted-dark">
            {t('consents.loadingList')}
          </p>
        </div>
      ) : consents.length === 0 ? (
        <div className="rounded-xl border border-surface-border-light bg-surface-card-light px-5 py-12 text-center dark:border-surface-border-dark dark:bg-surface-card-dark">
          <svg
            className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h2 className="mt-3 text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
            {t('consents.empty.title')}
          </h2>
          <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
            {t('consents.empty.description')}
          </p>
        </div>
      ) : (
        <>
          <div className="consents-desktop-table overflow-hidden rounded-xl border border-surface-border-light bg-surface-card-light shadow-sm dark:border-surface-border-dark dark:bg-surface-card-dark">
            <table
              className="min-w-full divide-y divide-surface-border-light dark:divide-surface-border-dark"
              aria-label={t('consents.table.label')}
            >
              <thead className="bg-surface-2-light dark:bg-surface-2-dark">
                <tr>
                  {['type', 'grantedAt', 'status', 'ipAddress', 'actions'].map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted-light dark:text-text-muted-dark ${column === 'actions' ? 'text-right' : ''}`}
                    >
                      {t(`consents.table.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {consents.map((consent) => (
                  <tr key={consent.id}>
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                        {formatConsentType(consent.type)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm text-text-primary-light dark:text-text-primary-dark">
                      {formatDate(consent.granted_at)}
                    </td>
                    <td className="px-5 py-4">{renderStatus(consent)}</td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm text-text-muted-light dark:text-text-muted-dark">
                      {getIpAddressLabel(consent)}
                    </td>
                    <td className="px-5 py-2 text-right">{renderAction(consent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="consents-mobile-cards space-y-3" aria-label={t('consents.table.label')}>
            {consents.map((consent) => (
              <li
                key={consent.id}
                className="rounded-xl border border-surface-border-light bg-surface-card-light p-4 shadow-sm dark:border-surface-border-dark dark:bg-surface-card-dark"
              >
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
                  <div className="min-w-0">
                    <h2 className="break-words text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                      {formatConsentType(consent.type)}
                    </h2>
                  </div>
                  {renderStatus(consent)}
                </div>
                <dl className="mt-4 grid grid-cols-1 gap-3 border-t border-surface-border-light pt-3 dark:border-surface-border-dark">
                  <div>
                    <dt className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark">
                      {t('consents.table.grantedAt')}
                    </dt>
                    <dd className="mt-1 text-sm text-text-primary-light dark:text-text-primary-dark">
                      {formatDate(consent.granted_at)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark">
                      {t('consents.table.ipAddress')}
                    </dt>
                    <dd className="mt-1 break-all text-sm text-text-primary-light dark:text-text-primary-dark">
                      {getIpAddressLabel(consent)}
                    </dd>
                  </div>
                  <div className="flex justify-end border-t border-surface-border-light pt-2 dark:border-surface-border-dark">
                    {renderAction(consent)}
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </>
      )}

      <aside className="mt-6 rounded-xl border border-state-info-light/20 bg-state-info-bg-light p-4 text-state-info-light dark:border-state-info-dark/20 dark:bg-state-info-bg-dark dark:text-state-info-dark sm:p-5">
        <h2 className="text-sm font-semibold">{t('consents.info.title')}</h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-5">
          <li>{t('consents.info.required')}</li>
          <li>{t('consents.info.optional')}</li>
          <li>{t('consents.info.reinstatement')}</li>
          <li>
            {t('consents.info.deleteAccountPrefix')}{' '}
            <Link
              to="/delete-account"
              className="font-semibold underline underline-offset-2 hover:no-underline"
            >
              {t('consents.info.deleteAccountLink')}
            </Link>
            .
          </li>
        </ul>
      </aside>
    </div>
  );
}
